import React, { useCallback, useContext, useEffect, useRef, useState, Suspense } from 'react';
import { BranchesContext } from '../../context/BranchesContext';
import { NewRequestContext } from '../../context/NewRequestContext';
import { Marker, InfoBox } from '@react-google-maps/api';
import { CustomButton } from '../CustomButton';
import { Spinner } from 'react-bootstrap';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
// import { MarkerInfoBox } from './MarkerInfoBox';
import { MarkerInfoBox } from './MarkerInfoBox';
import { NewRequest } from '../../pages/NewRequest';
import { MARKERS } from '../../utils/Enums/Markers';
import { MapBranch } from '../../models/dashboard-models';

type Props = {
    markers: MapBranch[];
    clusterer: any;
    isNewMarkerVissible: boolean;
    setIsNewMarkerVissible: React.Dispatch<React.SetStateAction<boolean>>;
    mapReadyForInfoBox: boolean;
};

export const OptimizedMarkers = ({ markers, clusterer, isNewMarkerVissible, setIsNewMarkerVissible, mapReadyForInfoBox }: Props) => {
    const {
        newMarker,
        lastSelectedBranch,
        setLastSelectedBranch,
        selectBranch,
        selectedBranches,
    } = useContext(BranchesContext);

    const { onLocationClick } = useContext(NewRequestContext);
    const [showNewRequestModal, setShowNewRequestModal] = useState(false);
    const markerRefs = useRef<Map<number, google.maps.Marker>>(new Map());
    const markerBranchesRef = useRef<Map<number, MapBranch>>(new Map());
    const selectedBranchIdsRef = useRef<Set<number>>(new Set());
    const previousMarkerIdsRef = useRef<Set<number>>(new Set());
    const lastSelectedBranchRef = useRef(lastSelectedBranch);
    const selectBranchRef = useRef(selectBranch);
    const [selectedMarker, setSelectedMarker] = useState<MapBranch | null>(null);

    const getSelectedBranch = useCallback(
        (id: number) => selectedBranches.find((branch) => branch.id === id) ?? markers.find((branch) => branch.id === id),
        [markers, selectedBranches]
    );
    const isSelected = useCallback((id: number) => selectedBranches.some((sel) => sel.id === id), [selectedBranches]);

    useEffect(() => {
        lastSelectedBranchRef.current = lastSelectedBranch;
        selectBranchRef.current = selectBranch;
    }, [lastSelectedBranch, selectBranch]);

    const getMarkerIcon = useCallback((branch: MapBranch, selected: boolean) => ({
        url: selected ? MARKERS.highlight : MARKERS[branch.state?.slice(1)],
        scaledSize: new google.maps.Size(23, 40),
    }), []);

    const addClusterMarkers = useCallback((newMarkers: google.maps.Marker[]) => {
        if (newMarkers.length === 0) {
            return;
        }

        if (typeof clusterer.addMarkers === 'function') {
            clusterer.addMarkers(newMarkers);
            return;
        }

        newMarkers.forEach((marker) => clusterer.addMarker(marker));
    }, [clusterer]);

    const removeClusterMarkers = useCallback((removedMarkers: google.maps.Marker[]) => {
        if (removedMarkers.length === 0) {
            return;
        }

        if (typeof clusterer.removeMarkers === 'function') {
            clusterer.removeMarkers(removedMarkers);
        } else {
            removedMarkers.forEach((marker) => clusterer.removeMarker(marker));
        }

        if (typeof clusterer.repaint === 'function') {
            clusterer.repaint();
        }
    }, [clusterer]);

    
 
    //  const getSelectedBranches = (id: number) => {
    //      const isSelected = selectedBranches.find((selected) => selected.id === id);
    //      return !!isSelected;
    //  };
 
    useEffect(() => {
        const storedMarkerId = localStorage.getItem('markerID');
        if (storedMarkerId) {
            setLastSelectedBranch(Number(storedMarkerId));
        } else {
            setLastSelectedBranch(null);
        }
    }, [setLastSelectedBranch]);

    useEffect(() => {
        if (!lastSelectedBranch) {
            setSelectedMarker(null);
            return;
        }

        const marker = markers.find((branch) => branch.id === lastSelectedBranch);
        if (marker) {
            setSelectedMarker(marker);
        }
    }, [lastSelectedBranch, markers]);

     useEffect(() => {
         const modalState = localStorage.getItem('isModalOpen');
         if (modalState === 'true') {
             setShowNewRequestModal(true);
         }
     }, []);
     useEffect(() => {
         localStorage.setItem('isModalOpen', JSON.stringify(showNewRequestModal));
     }, [showNewRequestModal]);

    useEffect(() => {
        const incomingIds = new Set(markers.map((branch) => branch.id));
        const removedIds = Array.from(previousMarkerIdsRef.current).filter((branchId) => !incomingIds.has(branchId));
        const shouldRebuildMarkerLayer = removedIds.length > 0;

        if (shouldRebuildMarkerLayer) {
            const currentMarkers = Array.from(markerRefs.current.values());
            if (typeof clusterer.clearMarkers === 'function') {
                clusterer.clearMarkers();
            } else {
                removeClusterMarkers(currentMarkers);
            }
            currentMarkers.forEach((marker) => {
                google.maps.event.clearInstanceListeners(marker);
                marker.setVisible(false);
                marker.setMap(null);
            });
            markerRefs.current.clear();
            markerBranchesRef.current.clear();
        }

        const removedMarkers: google.maps.Marker[] = [];

        if (!shouldRebuildMarkerLayer) {
            markerRefs.current.forEach((marker, branchId) => {
                if (!incomingIds.has(branchId)) {
                    google.maps.event.clearInstanceListeners(marker);
                    marker.setVisible(false);
                    marker.setMap(null);
                    markerRefs.current.delete(branchId);
                    markerBranchesRef.current.delete(branchId);
                    removedMarkers.push(marker);
                }
            });
            removeClusterMarkers(removedMarkers);
        }

        const newMarkers: google.maps.Marker[] = [];
        markers.forEach((branch) => {
            const position = { lat: +branch.latitude, lng: +branch.longitude };
            const title = `Branch ${branch.name ?? branch.id}`;
            const existingMarker = markerRefs.current.get(branch.id);
            const selected = selectedBranchIdsRef.current.has(branch.id);
            markerBranchesRef.current.set(branch.id, branch);

            if (existingMarker) {
                existingMarker.setPosition(position);
                existingMarker.setTitle(title);
                existingMarker.setIcon(getMarkerIcon(branch, selected));
                google.maps.event.clearInstanceListeners(existingMarker);
                existingMarker.addListener('click', () => {
                    localStorage.setItem('markerID', branch.id.toString());
                    if (lastSelectedBranchRef.current === branch.id) return;

                    void selectBranchRef.current(branch).then(() => {
                        setSelectedMarker(branch);
                    });
                });
                return;
            }

            const marker = new google.maps.Marker({
                position,
                icon: getMarkerIcon(branch, selected),
                title,
            });

            marker.addListener('click', () => {
                localStorage.setItem('markerID', branch.id.toString());
                if (lastSelectedBranchRef.current === branch.id) return;

                void selectBranchRef.current(branch).then(() => {
                    setSelectedMarker(branch);
                });
            });

            markerRefs.current.set(branch.id, marker);
            newMarkers.push(marker);
        });
        addClusterMarkers(newMarkers);
        previousMarkerIdsRef.current = incomingIds;
    }, [addClusterMarkers, getMarkerIcon, markers, removeClusterMarkers]);

    useEffect(() => {
        const nextSelectedIds = new Set(selectedBranches.map((branch) => branch.id));
        const idsToUpdate = new Set([...selectedBranchIdsRef.current, ...nextSelectedIds]);
        selectedBranchIdsRef.current = nextSelectedIds;

        idsToUpdate.forEach((branchId) => {
            const marker = markerRefs.current.get(branchId);
            const branch = markerBranchesRef.current.get(branchId);
            if (!marker || !branch) {
                return;
            }

            marker.setIcon(getMarkerIcon(branch, nextSelectedIds.has(branchId)));
        });
    }, [getMarkerIcon, selectedBranches]);

    useEffect(() => {
        return () => {
            const currentMarkers = Array.from(markerRefs.current.values());
            removeClusterMarkers(currentMarkers);
            currentMarkers.forEach((marker) => {
                google.maps.event.clearInstanceListeners(marker);
                marker.setMap(null);
            });
            markerRefs.current.clear();
            markerBranchesRef.current.clear();
            previousMarkerIdsRef.current.clear();
        };
    }, [removeClusterMarkers]);

    return (
        <>
            {/* New Marker */}
            {newMarker?.lat && newMarker?.lng && (
                <Marker visible={isNewMarkerVissible} position={new google.maps.LatLng(newMarker.lat, newMarker.lng)}>
                    {isNewMarkerVissible && mapReadyForInfoBox && (
                        <InfoBox options={{ closeBoxURL: '' }}>
                            <div className='d-flex flex-column w-100 '>
                                <CustomButton
                                    title='Cancel'
                                    iconClass='me-2'
                                    onClick={() => {
                                        localStorage.removeItem('isModalOpen');
                                        setIsNewMarkerVissible(false);
                                    }}
                                    className='color-main text-nowrap outline-none px-3 new-request-button border-bottom-0 rounded-0'
                                />
                                <CustomButton
                                    icon={faPlus}
                                    title='New Request'
                                    iconClass='me-2'
                                    onClick={() => {
                                        setShowNewRequestModal(true);
                                        onLocationClick(String(newMarker.lat), String(newMarker.lng));
                                    }}
                                    className='color-main text-nowrap outline-none px-3 new-request-button rounded-0'
                                />
                            </div>
                        </InfoBox>
                    )}
                </Marker>
            )}

            {/* InfoBox for selected marker */}
            {selectedMarker && selectedMarker?.id === lastSelectedBranch && getSelectedBranch(selectedMarker.id) && (
                <Suspense
                    fallback={
                        <div className='info-box-content bg-white position-absolute rounded overflow-hidden'>
                            <div className='marker-suspense'>
                                <Spinner animation='border' />
                            </div>
                        </div>
                    }
                >
                    <MarkerInfoBox selectedBranch={getSelectedBranch(selectedMarker.id) as MapBranch} />
                </Suspense>
            )}

            {/* Modal */}
            {showNewRequestModal && (
                <NewRequest
                    showModal={showNewRequestModal}
                    setShowModal={setShowNewRequestModal}
                    setIsNewMarkerVissible={setIsNewMarkerVissible}
                />
            )}
        </>
    );
};
